/**
 * PasswordReset component.
 * @module components/theme/PasswordReset/PasswordReset
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Link, withRouter } from 'react-router-dom';
import { BodyClass, Helmet } from '@plone/volto/helpers';
import { Button, Container, Form, Segment } from 'semantic-ui-react';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';

import { setInitialPassword } from '@plone/volto/actions';
import config from '@plone/volto/registry';

const messages = defineMessages({
  title: {
    id: 'Set your password',
    defaultMessage: 'Set your password',
  },
  description: {
    id: 'Please fill out the form below to set your password.',
    defaultMessage: 'Please fill out the form below to set your password.',
  },
  default: {
    id: 'Default',
    defaultMessage: 'Default',
  },
  usernameTitle: {
    id: 'My username is',
    defaultMessage: 'My username is',
  },
  emailTitle: {
    id: 'My email is',
    defaultMessage: 'My email is',
  },
  usernameDescription: {
    id: 'Enter your username for verification.',
    defaultMessage: 'Enter your username for verification.',
  },
  emailDescription: {
    id: 'Enter your email for verification.',
    defaultMessage: 'Enter your email for verification.',
  },
  passwordTitle: {
    id: 'New password',
    defaultMessage: 'New password',
  },
  passwordDescription: {
    id: 'Enter your new password. Minimum 5 characters.',
    defaultMessage: 'Enter your new password. Minimum 5 characters.',
  },
  passwordRepeatTitle: {
    id: 'Confirm password',
    defaultMessage: 'Confirm password',
  },
  passwordsDoNotMatch: {
    id: 'Passwords do not match.',
    defaultMessage: 'Passwords do not match.',
  },
  mailDoNotMatch: {
    id: 'E-mail addresses do not match.',
    defaultMessage: 'E-mail addresses do not match.',
  },
  passwordRepeatDescription: {
    id: 'Re-enter the password. Make sure the passwords are identical.',
    defaultMessage:
      'Re-enter the password. Make sure the passwords are identical.',
  },
  setPassword: {
    id: 'Set Password',
    defaultMessage: 'Set Password',
  },
  successRedirectToLoginTitle: {
    id: 'Account activation completed',
    defaultMessage: 'Account activation completed',
  },
  successRedirectToLoginBody: {
    id:
      'Your password has been set successfully. You may now {link} with your new password.',
    defaultMessage:
      'Your password has been set successfully. You may now {link} with your new password.',
  },
  passwordReset: {
    id: 'Password reset',
    defaultMessage: 'Password reset',
  },
});

/**
 * PasswordReset class.
 * @class PasswordReset
 * @extends Component
 */
class PasswordReset extends Component {
  /**
   * Property types.
   * @property {Object} propTypes Property types.
   * @static
   */
  static propTypes = {
    loading: PropTypes.bool.isRequired,
    loaded: PropTypes.bool.isRequired,
    error: PropTypes.string,
    token: PropTypes.string.isRequired,
    setInitialPassword: PropTypes.func.isRequired,
  };

  /**
   * Default properties.
   * @property {Object} defaultProps Default properties.
   * @static
   */
  static defaultProps = {
    error: null,
  };

  /**
   * Constructor
   * @method constructor
   * @param {Object} props Component properties
   * @constructs Controlpanel
   */
  constructor(props) {
    super(props);
    this.onSubmit = this.onSubmit.bind(this);
    this.state = {
      error: null,
      isSuccessful: false,
    };
  }

  /**
   * Component will receive props
   * @method componentWillReceiveProps
   * @param {Object} nextProps Next properties
   * @returns {undefined}
   */
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.loading && nextProps.loaded) {
      this.setState({ isSuccessful: true });
    }
  }

  /**
   * Submit handler
   * @method onSubmit
   * @param {object} data Form data.
   * @param {object} event Form data.
   * @returns {undefined}
   */
  onSubmit(event) {
    this.props.setInitialPassword(
      document.getElementsByName('login')[0].value,
      this.props.token,
      document.getElementsByName('password')[0].value,
    );
    event.preventDefault();
  }

  /**
   * Render method.
   * @method render
   * @returns {string} Markup for the component.
   */
  render() {
    if (this.state.isSuccessful) {
      return (
        <div id="page-password-reset">
          <Helmet
            title={this.props.intl.formatMessage(messages.passwordReset)}
          />
          <BodyClass className="background-grey" />
          <Container>
            <div className="form">
              <Segment>
                <h1 className="documentFirstHeading">
                  <FormattedMessage id="Success!" defaultMessage="Success!" />
                </h1>
                <p className="description">
                  <FormattedMessage
                    id="Your password has been set successfully. You may now {link} with your new password."
                    defaultMessage="Your password has been set successfully. You may now {link} with your new password."
                    values={{
                      link: (
                        <Link to="/login">
                          {this.props.intl.formatMessage({ id: 'Log In' })}
                        </Link>
                      ),
                    }}
                  />
                </p>
              </Segment>
            </div>
          </Container>
        </div>
      );
    }
    if (this.props.token) {
      return (
        <div id="page-password-reset">
          <Helmet
            title={this.props.intl.formatMessage(messages.passwordReset)}
          />
          <BodyClass className="background-grey" />
          <Container>
            <Form method="post" className="form" onSubmit={this.onSubmit}>
              <Segment raised>
                <h1>
                  <FormattedMessage
                    id="Set my password"
                    defaultMessage="Set my password"
                  />
                </h1>
                <Form.Field>
                  <label htmlFor="login">
                    <FormattedMessage id="Email" defaultMessage="Email" />
                  </label>
                  <input
                    id="login"
                    name="login"
                    placeholder="e.g. john@do.com"
                    autoFocus
                  />
                </Form.Field>
                <Form.Field>
                  <label htmlFor="password">
                    <FormattedMessage id="Password" defaultMessage="Password" />
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    placeholder="your new password"
                    tabIndex={0}
                  />
                </Form.Field>
                <div className="button-bar">
                  <Button
                    primary
                    className="rounded"
                    type="submit"
                    id="login-form-submit"
                    aria-label={this.props.intl.formatMessage(
                      messages.setPassword,
                    )}
                    title={this.props.intl.formatMessage(messages.setPassword)}
                    loading={this.props.loading}
                  >
                    <FormattedMessage
                      id="Set Password"
                      default="Set Password"
                    />
                  </Button>
                </div>
              </Segment>
            </Form>
          </Container>
        </div>
      );
    }
    return <div />;
  }
}

export default compose(
  withRouter,
  injectIntl,
  connect(
    (state, props) => ({
      loading: state.users.initial.loading,
      loaded: state.users.initial.loaded,
      error: state.users.initial.error,
      token: props.match.params.token,
    }),
    { setInitialPassword },
  ),
)(PasswordReset);
